import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TagsLists from 'components/Dashboard/TagsList';
import useLoadCompanyImage from 'components/Dashboard/UserContent/useLoadCompanyImage';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';
import ColorThief from 'colorthief/dist/color-thief.umd';
import getAppSite from 'utils/getAppSite';
import useItemController from 'components/Dashboard/ItemController/useItemController';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import ContentTypeBadge from 'components/Dashboard/components/ContentTypeBadge';
import useLogoUrlReplacement from 'components/Dashboard/UserContent/ui/useLogoUrlReplacement';
import PriorityBadge from 'components/Dashboard/ItemController/PriorityBadge';
import RatingUpdateBadge from 'components/Dashboard/PriorityUpdates/RatingUpdateBadge';
import preventLink from 'components/Dashboard/TagsList/prevenLink';

const InfluenceItem = (props) => {
  const {
    item,
    array,
    index,
    feedItemFlags,
    scrollBarRef,
    hideYellowDot,
    showContentTypeBadge,
    hideImage,
    tagsFixedPosition,
    itemActionsProps,
    zIndex,
    onlyEmbargoedTag,
    tooltipText,
    showPriorityBadge,
    setActiveUpdate,
  } = props;
  const {
    title,
    organisation = item?.creatorOrganisation,
    link = item?.contentUrl,
    draft,
    contentType,
    type,
    clientKeywordListName,
  } = item;
  const imageUrl = item?.creatorOrganisationLogo ?? item?.clientLogo ?? item?.ownerOrganisationLogo;
  const { rememberPosition } = useRememberPosition();

  const feedItem = feedItemFlags?.[index];
  const matchingKeywordLists = feedItem?.matchingKeywordLists ?? null;

  const isMobile = useIsMobile();

  const { unseenConditional, locationDescriptor, priorityBadgeCondition, rateUpdatesMode } = useItemController({
    item,
    index,
    hideYellowDot,
    showPriorityBadge,
  });

  return (
    <Link
      className={`box-rounded main-link-item influence-list-item box-list-item content-item-link ${
        showContentTypeBadge ? 'content-item' : ''
      }`}
      to={locationDescriptor(link ?? item.contentUrl)}
      style={{ zIndex: zIndex ?? (array ? array.length - index : 1) }}
      onClick={(e) => {
        if (rateUpdatesMode && setActiveUpdate) {
          preventLink(e);
          setActiveUpdate({ ...item, importanceRating: feedItemFlags?.[index]?.importanceRating });
          return;
        }
        if (scrollBarRef) {
          rememberPosition('Influence', scrollBarRef, index);
        }
      }}
      data-tooltip-content={tooltipText}
      data-tooltip-id={'general-tooltip'}
      data-tooltip-class-name='header-tooltip'
      data-tooltip-place='top'
    >
      <div className={`${showContentTypeBadge ? 'py-3 px-4' : 'main-link-item-padding'}`}>
        {unseenConditional && (
          <>
            <span className='unseen-dot' />
          </>
        )}
        {showContentTypeBadge && (
          <div className='d-flex align-items-baseline justify-content-between'>
            <div className='mt-1 specific-bottom-margin-12 flex-grow-1 text-light-blue blue-icon content-type-badge flex-centered justify-content-between'>
              <ContentTypeBadge objectType={item.type} name={TransformFromPascalCase(contentType ?? type)} />
            </div>
            <div className='flex-centered'>
              {priorityBadgeCondition && <PriorityBadge item={item} feedItemFlags={feedItemFlags[index]} />}
              {!draft && (
                <GeneralItemActions
                  item={item}
                  index={index}
                  influenceItem={true}
                  itemActionsProps={itemActionsProps}
                />
              )}
            </div>
          </div>
        )}
        <div className='d-flex align-items-start'>
          {!isMobile && !hideImage && <OrganisationLogo imageUrl={imageUrl} index={index} />}
          <div className={`influence-item_title ${isMobile || hideImage ? 'w-100' : ''} ${hideImage ? 'pl-0' : ''}`}>
            <div
              className={`d-flex align-items-baseline justify-content-between specific-bottom-padding-4 specific-bottom-margin-${
                showContentTypeBadge ? '8' : '14'
              } ${showContentTypeBadge ? '' : 'border-bottom'}`}
            >
              <h4 className='content-item-title mb-0 pr-lg-4'>
                {draft && <span className='text-red font-weight-bold'>Draft: </span>}
                {title}
              </h4>

              {!showContentTypeBadge && (
                <div className='flex-centered'>
                  {rateUpdatesMode ? (
                    <>
                      {feedItemFlags?.[index]?.importanceRating && (
                        <RatingUpdateBadge
                          item={{ rating: TransformFromPascalCase(feedItemFlags?.[index]?.importanceRating) }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {!isMobile && (
                        <p className='paragraph-p4-bold text-light-blue mb-0 mr-2 text-right influence-type-text'>
                          {TransformFromPascalCase(contentType ?? type)}
                        </p>
                      )}
                      {priorityBadgeCondition && <PriorityBadge item={item} feedItemFlags={feedItemFlags[index]} />}
                      {!draft && (
                        <GeneralItemActions
                          item={item}
                          index={index}
                          influenceItem={true}
                          itemActionsProps={itemActionsProps}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className='d-flex d-lg-block align-items-start'>
              {isMobile && <OrganisationLogo imageUrl={imageUrl} index={index} />}
              <div className='pl-3 pl-lg-0 flex-grow-1'>
                <p className='paragraph-p2 mb-0'>
                  <strong>Lead {getAppSite() === 'usa' ? 'organization' : 'organisation'}: </strong>
                  <span>{clientKeywordListName ?? organisation}</span>
                </p>
                {!!matchingKeywordLists && matchingKeywordLists.length > 0 && (
                  <div className='pt-2'>
                    <TagsLists
                      {...props}
                      feedItemFlags={feedItemFlags}
                      index={index}
                      section={'Influence'}
                      tagsFixedPosition={tagsFixedPosition}
                      scrollBarRef={scrollBarRef}
                      onlyEmbargoedTag={onlyEmbargoedTag}
                    />
                  </div>
                )}
                {isMobile && (
                  <p className='paragraph-p4-bold text-light-blue mt-1'>
                    {TransformFromPascalCase(contentType ?? type)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

const OrganisationLogo = (props) => {
  const { imageUrl, size, className = '', style = null, notShowDefaultImage } = props;
  const { replacementOfUrl, createImageStateUrl } = useLogoUrlReplacement();
  const imageState = useLoadCompanyImage(replacementOfUrl({ url: imageUrl }), size);
  const [whiteImage, setWhiteImage] = useState(false);

  const imageElement = useRef(null);
  const getLogoData = useRef(null);

  getLogoData.current = () => {
    if (!!imageUrl && !!imageElement?.current) {
      setTimeout(() => {
        const ct = new ColorThief();
        const img = imageElement.current;
        if (img?.complete) {
          try {
            ct.getColor(img);
          } catch (error) {
            setWhiteImage(true);
          }
        } else {
          img?.addEventListener('load', () => {
            try {
              ct.getColor(img);
            } catch (error) {
              setWhiteImage(true);
            }
          });
        }
      }, 500);
    }
  };

  useEffect(() => {
    if (!imageState.loading) {
      getLogoData.current();
    }
  }, [imageState.loading]);

  const imageStateUrl = createImageStateUrl(imageState);
  return (
    <>
      {!imageState.loading && (!imageState?.defaultImage || (imageState?.defaultImage && !notShowDefaultImage)) && (
        <div className={`${whiteImage ? 'influence-white-image-container' : ''} ${className}`}>
          <img
            style={style}
            className='influence-list-image'
            ref={imageElement}
            crossOrigin='anonymous'
            src={`${imageStateUrl}`}
            alt='logotype'
          />
        </div>
      )}
    </>
  );
};

export { OrganisationLogo };
export default InfluenceItem;
